/**
 * Hook que devuelve los roles del usuario actual desde la tabla user_roles.
 * Cachea con TanStack Query.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useUser } from '@/lib/auth';
import type { AppRole } from '@gurumba/schemas';

export function useUserRoles() {
  const user = useUser();
  return useQuery({
    queryKey: ['user-roles', user?.id],
    queryFn: async (): Promise<AppRole[]> => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);
      if (error) throw error;
      return (data ?? []).map((r) => r.role as AppRole);
    },
    enabled: !!user,
    staleTime: 5 * 60_000, // 5 min — los roles cambian rara vez
  });
}

export function useIsGuru(): boolean {
  const { data } = useUserRoles();
  return !!data?.includes('guru');
}

export function useIsAdmin(): boolean {
  const { data } = useUserRoles();
  return !!data?.includes('admin');
}

// =====================================================================
// Sprint 10.23 — Capabilities (sub-roles administrativos)
// =====================================================================
// Cada hook devuelve true si el user es admin (super-role) o tiene la
// capability específica. admin > capability, siempre.

export function useIsModerator(): boolean {
  const { data } = useUserRoles();
  return !!data?.some((r) => r === 'admin' || r === 'moderator');
}

export function useIsSupportAgent(): boolean {
  const { data } = useUserRoles();
  return !!data?.some((r) => r === 'admin' || r === 'support_agent');
}

export function useIsPartnerViewer(): boolean {
  const { data } = useUserRoles();
  return !!data?.some((r) => r === 'admin' || r === 'partner_viewer');
}

/** Socio con acceso al módulo financiero (24-sep-2026). */
export function useIsPartnerFinance(): boolean {
  const { data } = useUserRoles();
  return !!data?.some((r) => r === 'admin' || r === 'partner_finance');
}

export function useIsContentEditor(): boolean {
  const { data } = useUserRoles();
  return !!data?.some((r) => r === 'admin' || r === 'content_editor');
}

/** True si el user tiene admin o cualquiera de los sub-roles del panel.
 *  Determina si ve el sidebar admin (filtrado luego por capability). */
export function useHasAdminPanelAccess(): boolean {
  const { data } = useUserRoles();
  if (!data) return false;
  return data.some(
    (r) =>
      r === 'admin' ||
      r === 'moderator' ||
      r === 'support_agent' ||
      r === 'partner_viewer' ||
      r === 'partner_finance' ||
      r === 'content_editor'
  );
}

/** Devuelve un objeto con cada capability como boolean — útil para
 *  filtrar items del sidebar de una vez. */
export interface UserCapabilities {
  isAdmin: boolean;
  isModerator: boolean;
  isSupportAgent: boolean;
  isPartnerViewer: boolean;
  /** Socio · Finanzas: ve /admin/finanzas (y nada operativo). */
  isPartnerFinance: boolean;
  isContentEditor: boolean;
  hasAnyAdminAccess: boolean;
  /** Socio "puro": tiene rol de socio y ningún otro permiso del panel. */
  isPartnerOnly: boolean;
}

export function useCapabilities(): UserCapabilities {
  const { data } = useUserRoles();
  const roles = data ?? [];
  const isAdmin = roles.includes('admin');
  const isPartner = roles.includes('partner_viewer') || roles.includes('partner_finance');
  const hasOperationalRole =
    roles.includes('moderator') || roles.includes('support_agent') || roles.includes('content_editor');
  return {
    isAdmin,
    isModerator: isAdmin || roles.includes('moderator'),
    isSupportAgent: isAdmin || roles.includes('support_agent'),
    isPartnerViewer: isAdmin || roles.includes('partner_viewer'),
    isPartnerFinance: isAdmin || roles.includes('partner_finance'),
    isContentEditor: isAdmin || roles.includes('content_editor'),
    hasAnyAdminAccess: isAdmin || isPartner || hasOperationalRole,
    isPartnerOnly: !isAdmin && isPartner && !hasOperationalRole,
  };
}

/**
 * Modelo B (Sprint 8): un user tiene UN rol "primario" que define su mundo.
 *   - admin → puede acceder a todo (incluye guru y cliente, para QA)
 *   - guru  → solo /guru (no ve catálogo de cursos ni puede bookear)
 *   - user  → cliente normal (catálogo, gurus, sesiones)
 *
 * Si tiene varios roles, admin gana sobre guru, y guru sobre user.
 * Devuelve null mientras los roles están cargando.
 */
export type PrimaryRole = 'admin' | 'guru' | 'user';

export function usePrimaryRole(): PrimaryRole | null {
  const { data, isLoading } = useUserRoles();
  if (isLoading || !data) return null;
  if (data.includes('admin')) return 'admin';
  if (data.includes('guru')) return 'guru';
  return 'user';
}
